/**
 * useMeasureThresholdMap
 *
 * Wraps `useMeasureSchedule` and exposes a keyed lookup for the operational
 * and display thresholds of each (measure, bonusEligibleType) pair for the
 * given year + quarter.
 *
 * This is the single canonical way for UI components to resolve
 * thresholds. Consumers should never hardcode 89/90 or similar values —
 * use this hook so that changes in `och.equip_config_measure_schedule`
 * flow through to the UI without code changes.
 *
 * The lookup key is `${measureName}|${bonusEligibleType}` (case-sensitive).
 * Measure name casing must match the API response (e.g., `BCS`, `ACO_Visits`).
 */

import { useMemo } from 'react'
import { useMeasureSchedule } from './useEquipConfig'
import type { MeasureScheduleItem } from '@/lib/equip-types'

/** A pair of thresholds stored on the config table, both in 0–1 decimal form. */
export interface ThresholdPair {
  /** Operational threshold used for pass/fail. e.g., 0.89 for the 90% tier. */
  threshold: number
  /** Display threshold shown to users. e.g., 0.90. */
  thresholdDisplay: number
}

/**
 * Keyed lookup returned by `useMeasureThresholdMap`.
 *
 * Usage:
 *   const map = useMeasureThresholdMap(year, quarter)
 *   const pair = map.get('BCS', 'PCP')  // { threshold: 0.44, thresholdDisplay: 0.45 } in Q2
 */
export interface MeasureThresholdMap {
  get: (measureName: string, bonusEligibleType: string) => ThresholdPair | undefined
  isLoading: boolean
  isError: boolean
  /** Raw schedule items, exposed for callers that need weight / category / active flags. */
  items: MeasureScheduleItem[]
}

function buildKey(measureName: string, bonusEligibleType: string): string {
  return `${measureName}|${bonusEligibleType}`
}

export function useMeasureThresholdMap(year: number, quarter: number): MeasureThresholdMap {
  const query = useMeasureSchedule(year, quarter)

  const lookup = useMemo<Map<string, ThresholdPair>>(() => {
    const map = new Map<string, ThresholdPair>()
    if (!query.data) return map
    for (const row of query.data) {
      if (!row.isActive) continue
      map.set(buildKey(row.measureName, row.bonusEligibleType), {
        threshold: row.threshold,
        thresholdDisplay: row.thresholdDisplay,
      })
    }
    return map
  }, [query.data])

  const items = useMemo<MeasureScheduleItem[]>(
    () => query.data ?? [],
    [query.data]
  )

  return useMemo<MeasureThresholdMap>(
    () => ({
      get: (measureName: string, bonusEligibleType: string) =>
        lookup.get(buildKey(measureName, bonusEligibleType)),
      isLoading: query.isLoading,
      isError: query.isError,
      items,
    }),
    [lookup, query.isLoading, query.isError, items]
  )
}
